/* =========================================================================
   femilism-subscription-fix.js
   -------------------------------------------------------------------------
   WHY THIS EXISTS

   Sugar's subscriptions block parses the delivery frequency out of the
   Shopify selling plan option value like this (blocks/subscriptions.liquid,
   around line 2554 and repeated five more times):

       {%- assign parts = option.value | split: ' ' -%}

   It splits on a SPACE, then looks through the parts for a number and for a
   time unit. Recharge writes the value with a HYPHEN — "1-month" — under the
   option name "Order Frequency and Unit". Splitting "1-month" on a space
   yields one chunk that is neither a number nor a unit, so the block's
   `unit` stays empty, its replace never fires, and the raw placeholder
   "[number] [unit]" is rendered to the page.

   Recharge confirmed those option names and the combined "1-month" format
   are hardcoded in their selling plan sync and are not configurable — not in
   their admin, not via the plans API, not by their support team. Renaming
   the plan does not change them. So the fix has to be theme-side.

   The correct fix is one character in subscriptions.liquid: split on hyphens
   too. That file is 205KB and the Shopify API only writes whole files, so
   this shim does the same job from the outside instead. If Sugar ever ships
   a fix, delete this file and its <script> tag in layout/theme.liquid.

   HOW IT WORKS
   Reads the real frequency from the product JSON at /products/<handle>.js,
   which exposes every selling plan's option values, then substitutes the
   placeholders wherever they were rendered.
   ========================================================================= */
(function () {
  'use strict';

  var UNITS = ['day', 'week', 'month', 'year'];

  /* "1-month" / "2 weeks" / "month" -> { number: '1', unit: 'month' } */
  function parseFrequency(value) {
    if (!value) return null;
    var parts = String(value).toLowerCase().split(/[-\s_]+/);
    var number = '';
    var unit = '';

    parts.forEach(function (part) {
      var singular = part.replace(/s$/, '');
      if (UNITS.indexOf(singular) !== -1) {
        unit = singular;
      } else if (/^\d+$/.test(part) && part !== '0') {
        number = part;
      }
    });

    if (!unit) return null;
    if (!number) number = '1';
    return { number: number, unit: number === '1' ? unit : unit + 's' };
  }

  /* Pick the frequency out of whichever option holds it */
  function frequencyForPlan(plan) {
    var found = null;
    (plan.options || []).forEach(function (opt) {
      if (found) return;
      var value = typeof opt === 'string' ? opt : opt.value;
      found = parseFrequency(value);
    });
    return found;
  }

  function substitute(root, freq) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (node.nodeValue.indexOf('[number]') === -1 && node.nodeValue.indexOf('[unit]') === -1) {
        continue;
      }
      node.nodeValue = node.nodeValue
        .split('[number]').join(freq.number)
        .split('[unit]').join(freq.unit);
    }
  }

  function run(product) {
    var groups = product.selling_plan_groups || [];
    if (!groups.length) return;

    /* Map each selling plan id to its parsed frequency */
    var byId = {};
    var fallback = null;
    groups.forEach(function (group) {
      (group.selling_plans || []).forEach(function (plan) {
        var freq = frequencyForPlan(plan);
        if (!freq) return;
        byId[String(plan.id)] = freq;
        if (!fallback) fallback = freq;
      });
    });
    if (!fallback) return;

    /* <option> elements carry the selling plan id, so they can be exact */
    document.querySelectorAll('.subscription-frequency__dropdown option').forEach(function (opt) {
      var freq = byId[String(opt.value)] || fallback;
      if (opt.textContent.indexOf('[number]') !== -1 || opt.textContent.indexOf('[unit]') !== -1) {
        opt.textContent = opt.textContent
          .split('[number]').join(freq.number)
          .split('[unit]').join(freq.unit);
      }
    });

    /* Any other placeholder — locked label, titles, subtitles, badges.
       With a single plan the fallback is the right value; with several, these
       labels are rewritten by the block's own JS when the choice changes. */
    document.querySelectorAll('[class*="subscription"]').forEach(function (el) {
      substitute(el, fallback);
    });
  }

  function init() {
    if (!document.querySelector('[class*="subscription"]')) return;

    var handle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!handle) return;

    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'products/' + handle + '.js'
      : '/products/' + handle + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) { if (product) run(product); })
      .catch(function () { /* leave the page as-is rather than half-rewritten */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The block re-renders its labels when the customer switches between
     one-time and subscribe, which can reintroduce the placeholders. */
  document.addEventListener('click', function (e) {
    if (!e.target.closest || !e.target.closest('[class*="subscription"]')) return;
    setTimeout(init, 60);
  });
})();
